import { useEffect, useId, useRef, useState } from 'react';
import { cn } from '@/lib/cn';
import { Avatar } from '@/components/Avatar';
import { Button } from '@/ui/Button';
import { TIPOS } from './piezas';
import { type Filtros as Estado, useAutores } from './api';

/**
 * Filtros — acotar el muro por tipo, autor, texto y fecha.
 *
 * Los siete tipos tienen píldora. En la versión vanilla faltaban dos: se podía
 * PUBLICAR «Bienestar» sin poder filtrarlo, y los comunicados —justo lo que uno
 * querría localizar— no tenían ninguna. Se añadieron el 2026-08-26 y aquí salen
 * de `TIPOS`, así que un tipo nuevo trae su píldora sola.
 *
 * `evento_galeria` no tiene píldora **a propósito**: no es una categoría que
 * alguien busque, es el subproducto de un evento con fotos. Las galerías se ven
 * filtrando por «Eventos».
 */

const PERIODOS = [
  { valor: '', etiqueta: 'Cualquier fecha' },
  { valor: '7', etiqueta: 'Últimos 7 días' },
  { valor: '30', etiqueta: 'Últimos 30 días' },
  { valor: '90', etiqueta: 'Últimos 3 meses' },
];

/** El orden de las píldoras. `general` no está: es el caso por defecto y no una
 *  categoría que alguien filtre. */
const TIPOS_FILTRABLES = ['cumpleanos', 'evento', 'encuesta', 'logro', 'bienestar', 'comunicado'];

export function Filtros({
  valor, onCambio,
}: {
  valor: Estado;
  onCambio: (f: Estado) => void;
}) {
  return (
    <div className="flex flex-col gap-3">
      <BuscadorDeAutor
        autor={valor.autor}
        onElegir={(autor) => onCambio({ ...valor, autor })}
      />

      <div className="flex flex-wrap items-center gap-2">
        <label className="relative min-w-45 flex-1 sm:max-w-90">
          <span className="sr-only">Buscar en publicaciones</span>
          <i
            className="bi bi-search pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            aria-hidden="true"
          />
          <input
            type="search"
            value={valor.busqueda}
            onChange={(e) => onCambio({ ...valor, busqueda: e.target.value })}
            placeholder="Buscar en publicaciones…"
            className="w-full rounded-sm border border-field-border bg-surface py-2 pl-9 pr-3
                       text-3 text-ink focus-visible:focus-anillo pointer-coarse:min-h-11"
          />
        </label>

        <label>
          <span className="sr-only">Período de las publicaciones</span>
          <select
            value={valor.dias ? String(valor.dias) : ''}
            onChange={(e) =>
              onCambio({ ...valor, dias: e.target.value ? Number(e.target.value) : null })
            }
            className="rounded-sm border border-field-border bg-surface px-3 py-2 text-3 text-ink
                       focus-visible:focus-anillo pointer-coarse:min-h-11"
          >
            {PERIODOS.map((p) => (
              <option key={p.valor} value={p.valor}>
                {p.etiqueta}
              </option>
            ))}
          </select>
        </label>
      </div>

      {/* La fila de píldoras se desplaza sola en móvil en vez de partirse en
          tres alturas: el muro es lo que la persona vino a leer, y la barra de
          filtros no debería comerse media pantalla. */}
      <div
        className="-mx-1 flex gap-1.5 overflow-x-auto px-1 pb-1"
        role="group"
        aria-label="Filtrar por tipo de publicación"
      >
        <Pildora
          activa={!valor.tipo}
          icono="bi-grid-fill"
          etiqueta="Todo"
          onClick={() => onCambio({ ...valor, tipo: '' })}
        />
        {TIPOS_FILTRABLES.map((clave) => {
          const meta = TIPOS[clave];
          if (!meta) return null;
          return (
            <Pildora
              key={clave}
              activa={valor.tipo === clave}
              icono={meta.icono}
              etiqueta={meta.etiqueta}
              onClick={() => onCambio({ ...valor, tipo: clave })}
            />
          );
        })}
      </div>
    </div>
  );
}

function Pildora({
  activa, icono, etiqueta, onClick,
}: {
  activa: boolean;
  icono: string;
  etiqueta: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={activa}
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-1.5',
        'text-2 font-semibold transition-colors focus-visible:focus-anillo',
        'pointer-coarse:min-h-11',
        activa
          ? 'border-brand-ink bg-brand text-on-brand'
          : 'border-line text-ink-2 hover:bg-sunken',
      )}
    >
      <i className={cn('bi', icono)} aria-hidden="true" />
      {etiqueta}
    </button>
  );
}

/* ── Buscador de autor ─────────────────────────────────────────────────── */

/**
 * Filtrar por quién publicó.
 *
 * El desplegable es una lista de `<button>`, no de `<div onclick>`: la versión
 * vanilla usaba `div` con `onmouseover`/`onmouseout` escritos como atributo, así
 * que el resaltado solo existía con ratón, no se podía elegir con teclado y el
 * lector de pantalla no anunciaba las opciones.
 *
 * Y el nombre entraba dos veces sin escapar: dentro de un `onclick` con un
 * `.replace(/'/g, '')` que hacía de escape —quita la comilla simple y deja
 * `"`, `<` y `&`— y luego en el banner de «Publicaciones de X».
 */
function BuscadorDeAutor({
  autor, onElegir,
}: {
  autor: Estado['autor'];
  onElegir: (a: Estado['autor']) => void;
}) {
  const [texto, setTexto] = useState('');
  const [abierto, setAbierto] = useState(false);
  const caja = useRef<HTMLDivElement>(null);
  const idLista = useId();
  const resultados = useAutores(texto);

  // Cerrar al pulsar fuera. En `pointerdown` y no en `click` para que se cierre
  // antes de que el clic llegue a lo que haya debajo.
  useEffect(() => {
    if (!abierto) return;
    const fuera = (e: PointerEvent) => {
      if (!caja.current?.contains(e.target as Node)) setAbierto(false);
    };
    document.addEventListener('pointerdown', fuera);
    return () => document.removeEventListener('pointerdown', fuera);
  }, [abierto]);

  if (autor) {
    return (
      <div className="flex flex-wrap items-center gap-2 rounded-sm border border-info/40 bg-info/10 px-3 py-2">
        <i className="bi bi-funnel-fill text-info-ink" aria-hidden="true" />
        <span className="min-w-0 flex-1 text-3 text-ink">
          Publicaciones de <strong>{autor.nombre}</strong>
        </span>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => {
            onElegir(null);
            setTexto('');
          }}
        >
          <i className="bi bi-x-lg" aria-hidden="true" /> Quitar filtro
        </Button>
      </div>
    );
  }

  const opciones = resultados.data ?? [];

  return (
    <div className="relative" ref={caja}>
      <label className="relative block">
        <span className="sr-only">Filtrar por autor</span>
        <i
          className="bi bi-person pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
          aria-hidden="true"
        />
        <input
          type="text"
          value={texto}
          role="combobox"
          aria-expanded={abierto && texto.trim().length >= 2}
          aria-controls={idLista}
          aria-autocomplete="list"
          onChange={(e) => {
            setTexto(e.target.value);
            setAbierto(true);
          }}
          onFocus={() => setAbierto(true)}
          onKeyDown={(e) => e.key === 'Escape' && setAbierto(false)}
          placeholder="Filtrar por autor…"
          className="w-full rounded-sm border border-field-border bg-surface py-2 pl-9 pr-3
                     text-3 text-ink focus-visible:focus-anillo pointer-coarse:min-h-11"
        />
      </label>

      {abierto && texto.trim().length >= 2 && (
        <ul
          id={idLista}
          role="listbox"
          aria-label="Personas encontradas"
          className="absolute left-0 right-0 top-full z-panel mt-1 max-h-60 overflow-y-auto
                     rounded-sm border border-line bg-surface shadow-overlay"
        >
          {resultados.isPending ? (
            <li className="px-3 py-2.5 text-3 text-muted">Buscando…</li>
          ) : !opciones.length ? (
            <li className="px-3 py-2.5 text-center text-3 text-muted">Sin resultados</li>
          ) : (
            opciones.map((u) => (
              <li key={u.id} role="option" aria-selected={false}>
                <button
                  type="button"
                  onClick={() => {
                    onElegir({ id: u.id, nombre: u.full_name });
                    setAbierto(false);
                  }}
                  className="flex w-full items-center gap-2.5 border-b border-line px-3 py-2
                             text-left transition-colors last:border-b-0 hover:bg-sunken
                             focus-visible:focus-anillo pointer-coarse:min-h-11"
                >
                  <Avatar nombre={u.full_name} tamano={30} />
                  <span className="min-w-0 flex-1 truncate text-3 font-semibold text-ink">
                    {u.full_name}
                  </span>
                  {u.position && (
                    <span className="shrink-0 truncate text-1 text-muted">{u.position}</span>
                  )}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
